// The Studio page — a scratch pad for scenes and films. It lists the stored
// ones (fetched from the REST API) and renders ANY pasted scene/clip JSON
// through the server's `preview` endpoints, which never write to the store. A
// scene renders to stills (iso + top); a clip renders to a storyboard contact
// sheet or a playable MP4. The same engine the CLI and MCP tools use, in the
// browser, with a paste box.
//
// A scene also opens in the live 3D view (see scene-view.ts): the same JSON,
// baked and drawn client-side, so you can orbit the composition or walk the
// camera through it instead of judging it from two fixed angles. That path
// needs the model catalogue in the browser, so it is loaded lazily (library.ts)
// the first time the 3D view is opened.

import type { SceneReport } from "../lib";
import type { SceneView } from "./scene-view";

type Kind = "scene" | "clip" | "unknown";

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;
const editor = $<HTMLTextAreaElement>("editor");
const kindBadge = $("kind-badge");
const runBtn = $<HTMLButtonElement>("run");
const view3dBtn = $<HTMLButtonElement>("run-3d");
const boardBtn = $<HTMLButtonElement>("run-board");
const videoBtn = $<HTMLButtonElement>("run-video");
const atWrap = $("at-wrap");
const atInput = $<HTMLInputElement>("at");
const statusEl = $("status");
const stage = $("stage");
const stageImg = $<HTMLImageElement>("stage-img");
const stageVideo = $<HTMLVideoElement>("stage-video");
const reportEl = $("report");
const progressEl = $("progress");
const progressLabel = $("progress-label");
const progressFill = $("progress-fill");
const progressPreview = $<HTMLImageElement>("progress-preview");
const progressCancel = $<HTMLButtonElement>("progress-cancel");
const view3dEl = $("view3d");

const escapeHtml = (s: string): string => s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]!));

function setStatus(msg: string, kind: "" | "busy" | "err" | "ok" = ""): void {
  statusEl.textContent = msg;
  statusEl.className = "status " + (kind === "err" ? "err" : kind === "ok" ? "ok" : "muted");
}

function detectKind(text: string): { kind: Kind; obj: Record<string, unknown> | null; err?: string } {
  const t = text.trim();
  if (!t) return { kind: "unknown", obj: null };
  let obj: Record<string, unknown>;
  try { obj = JSON.parse(t); } catch (e) { return { kind: "unknown", obj: null, err: "Not valid JSON: " + (e as Error).message }; }
  if (obj && Array.isArray((obj as { placements?: unknown }).placements)) return { kind: "scene", obj };
  if (obj && (Array.isArray((obj as { shots?: unknown }).shots) || Array.isArray((obj as { actors?: unknown }).actors))) return { kind: "clip", obj };
  return { kind: "unknown", obj };
}

function reflectKind(): void {
  const { kind, err } = detectKind(editor.value);
  kindBadge.textContent = err ? "invalid JSON" : kind === "scene" ? "scene" : kind === "clip" ? "film" : "no JSON";
  kindBadge.className = "kind kind-" + (err ? "err" : kind);
  const isClip = kind === "clip";
  boardBtn.hidden = !isClip;
  videoBtn.hidden = !isClip;
  atWrap.hidden = !isClip;
  // A film is walkable too — through the scene it plays in (fetched by id).
  view3dBtn.hidden = kind === "unknown";
  runBtn.textContent = isClip ? "Frame" : "Render";
}

async function postJSON(url: string, body: unknown): Promise<Record<string, unknown>> {
  const res = await fetch(url, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  if (!res.ok) throw new Error((data as { error?: string }).error || `HTTP ${res.status}`);
  return data as Record<string, unknown>;
}

function showImage(dataUrl: string): void {
  close3D();
  stageVideo.hidden = true; stageVideo.pause();
  stageImg.src = dataUrl; stageImg.hidden = false;
  stage.hidden = false;
}
function showVideo(url: string): void {
  close3D();
  stageImg.hidden = true;
  stageVideo.src = url; stageVideo.hidden = false;
  stage.hidden = false;
  void stageVideo.play().catch(() => {});
}

// ── Live render progress ────────────────────────────────────────────────────
// A film render takes minutes, so the server hands back a JOB instead of a
// video (see src/server/render-jobs.ts) and streams its progress over SSE.
// The page follows that stream: a bar, the counters, and the frame currently
// going into the encoder — so the wait shows what it is doing, and can be
// stopped.

interface RenderJob {
  id: string;
  status: "running" | "done" | "failed" | "cancelled";
  frame: number;
  total: number;
  percent: number;
  elapsed: number;
  eta: number | null;
  speed: number | null;
  url: string;
  error: string | null;
  previewFrame: number | null;
}

let liveRender: EventSource | null = null;

function showProgress(job: RenderJob | null): void {
  if (!job) {
    progressEl.hidden = true;
    progressPreview.hidden = true;
    progressPreview.removeAttribute("src");
    return;
  }
  progressEl.hidden = false;
  progressLabel.dataset.job = job.id;
  progressFill.style.width = `${Math.max(0, Math.min(100, job.percent))}%`;
  const left = job.eta != null ? `~${job.eta.toFixed(0)}s left` : `${job.elapsed.toFixed(1)}s`;
  progressLabel.textContent = job.status === "running"
    ? `frame ${job.frame}/${job.total} · ${job.percent.toFixed(0)}% · ${job.speed ? `${job.speed.toFixed(1)} fps · ` : ""}${left}`
    : `${job.status} · ${job.frame}/${job.total} frames in ${job.elapsed.toFixed(1)}s`;
  progressCancel.hidden = job.status !== "running";
  // The preview is a separate image so the JSON stream stays small; refetch it
  // whenever the server says a newer frame has been encoded.
  if (job.previewFrame != null && progressPreview.dataset.frame !== String(job.previewFrame)) {
    progressPreview.dataset.frame = String(job.previewFrame);
    progressPreview.src = `/api/renders/${encodeURIComponent(job.id)}/preview.png?f=${job.previewFrame}`;
    progressPreview.hidden = false;
  }
}

/** Follow a render job to its end, updating the progress panel as it goes. */
function followRender(job: RenderJob): Promise<RenderJob> {
  showProgress(job);
  return new Promise((resolve) => {
    const source = new EventSource(`/api/renders/${encodeURIComponent(job.id)}/events`);
    liveRender = source;
    let last = job;
    const stop = (final: RenderJob): void => {
      source.close();
      if (liveRender === source) liveRender = null;
      resolve(final);
    };
    source.onmessage = (event) => {
      last = JSON.parse(event.data) as RenderJob;
      showProgress(last);
      if (last.status !== "running") stop(last);
    };
    // A dropped stream is not a failed render: fall back to one status read.
    source.onerror = () => {
      void fetch(`/api/renders/${encodeURIComponent(job.id)}`)
        .then((r) => (r.ok ? r.json() as Promise<RenderJob> : last))
        .catch(() => last)
        .then((current) => { if (current.status !== "running") stop(current); });
    };
  });
}

progressCancel.addEventListener("click", () => {
  const id = progressLabel.dataset.job;
  if (id) void fetch(`/api/renders/${encodeURIComponent(id)}`, { method: "DELETE" });
});

interface Issue { severity: string; subject: string | null; message: string; }
interface Report { errors: number; warnings: number; notes?: number; issues: Issue[]; duration?: number; }

function renderReport(report: Report | null): void {
  if (!report || !Array.isArray(report.issues)) { reportEl.hidden = true; return; }
  const sev = (s: string): string => `<span class="sev sev-${s}">${s}</span>`;
  const rows = report.issues
    .map((i) => `<li>${sev(i.severity)} <span>${i.subject ? `<code>${escapeHtml(i.subject)}</code> ` : ""}${escapeHtml(i.message)}</span></li>`)
    .join("");
  const head = `${report.errors} error(s), ${report.warnings} warning(s), ${report.notes ?? 0} note(s)` +
    (report.duration != null ? ` · ${report.duration}s` : "");
  reportEl.innerHTML = `<div class="report-head">${head}</div>` + (rows ? `<ul>${rows}</ul>` : `<div class="muted">No issues.</div>`);
  reportEl.hidden = false;
}

async function render(mode: "auto" | "board" | "video"): Promise<void> {
  const { kind, obj, err } = detectKind(editor.value);
  if (err) { setStatus(err, "err"); return; }
  if (kind === "unknown" || !obj) { setStatus('Paste a scene (has "placements") or a film (has "shots").', "err"); return; }
  if (mode !== "video") showProgress(null);
  try {
    if (kind === "scene") {
      setStatus("Rendering scene…", "busy");
      const data = await postJSON("/api/scenes/preview", { scene: obj, views: "iso,top" });
      showImage(data.png as string); renderReport(data.report as Report); setStatus("Rendered.", "ok");
    } else if (mode === "video") {
      // The server renders in the background; this follows the job to the end.
      setStatus("Starting the render…", "busy");
      liveRender?.close();
      const data = await postJSON("/api/clips/preview-video", { clip: obj });
      renderReport(null);
      setStatus("Rendering the film…", "busy");
      const job = await followRender(data.job as unknown as RenderJob);
      if (job.status === "done") {
        showVideo(`${job.url}?t=${Date.now()}`);
        setStatus(`Film ready — ${job.total} frames in ${job.elapsed.toFixed(1)}s.`, "ok");
      } else if (job.status === "cancelled") {
        setStatus("Render stopped.", "");
      } else {
        setStatus(job.error ?? "Render failed.", "err");
      }
    } else if (mode === "board") {
      setStatus("Rendering storyboard…", "busy");
      const data = await postJSON("/api/clips/preview", { clip: obj, board: true });
      showImage(data.png as string); renderReport(data.report as Report); setStatus("Storyboard rendered.", "ok");
    } else {
      setStatus("Rendering frame…", "busy");
      const data = await postJSON("/api/clips/preview", { clip: obj, at: Number(atInput.value) || 0 });
      showImage(data.png as string); renderReport(data.report as Report); setStatus("Frame rendered.", "ok");
    }
  } catch (e) {
    setStatus((e as Error).message, "err");
  }
}

// ── The live 3D view ────────────────────────────────────────────────────────
// Built on first use: both the viewer module and the model catalogue it bakes
// against are pulled in lazily, so opening the Studio costs nothing until
// someone actually asks for 3D.
let view: SceneView | null = null;

async function ensureView(): Promise<SceneView> {
  if (view) return view;
  const [mod, { loadSceneLibrary }] = await Promise.all([import("./scene-view"), import("./library")]);
  view = new mod.SceneView($<HTMLCanvasElement>("view3d-canvas"), await loadSceneLibrary(), {
    onStats: (s) => {
      $("view3d-anim-wrap").hidden = !s.animated;
      setStatus(`3D — ${s.triangles.toLocaleString()} triangles, baked in ${s.ms} ms.`, "ok");
    },
    onLock: () => reflectHint(),
  });
  wire3D(view);
  return view;
}

/** Open whatever is in the editor in 3D: a scene directly, a film through the
 *  stored scene it plays in (so a shot's set is walkable too). */
async function open3D(): Promise<void> {
  const { kind, obj, err } = detectKind(editor.value);
  if (err) { setStatus(err, "err"); return; }
  let raw: unknown = obj;
  if (kind === "clip") {
    const sceneId = (obj as { scene?: unknown } | null)?.scene;
    if (typeof sceneId !== "string" || !sceneId) { setStatus('This film names no scene — add a "scene" id to walk its set.', "err"); return; }
    setStatus(`Loading the film's scene "${sceneId}"…`, "busy");
    const data = await fetch(`/api/scenes/${encodeURIComponent(sceneId)}`).then((r) => r.json()).catch(() => null);
    if (!data?.scene) { setStatus(data?.error || `No stored scene "${sceneId}" to walk.`, "err"); return; }
    raw = data.scene;
  } else if (kind !== "scene" || !obj) {
    setStatus('Paste a scene (has "placements") or a film (has "shots").', "err");
    return;
  }
  setStatus("Loading the models…", "busy");
  try {
    const [{ loadScene, analyzeScene }, { loadSceneLibrary }] = await Promise.all([import("../lib"), import("./library")]);
    const v = await ensureView();
    const scene = loadScene(raw);
    show3D();
    setStatus("Baking the scene…", "busy");
    // Yield a frame so the status paints before a big scene blocks on its bake.
    await new Promise((r) => requestAnimationFrame(r));
    v.show(scene); // reports the finished bake through onStats
    buildLevels(v);
    reflectHint();
    // The same verdict `validate_scene` gives, computed right here: baking
    // silently skips what it cannot resolve, so an unknown model would
    // otherwise just be a hole in the picture with no explanation.
    renderReport(sceneReport(analyzeScene(scene, await loadSceneLibrary())));
  } catch (e) {
    setStatus((e as Error).message, "err");
  }
}

/** The scene engine's own report in the shape the report box renders. */
function sceneReport(r: SceneReport): Report {
  return {
    errors: r.errors, warnings: r.warnings, notes: r.notes,
    issues: r.issues.map((i) => ({ severity: i.severity, subject: i.placement, message: i.message })),
  };
}

// What the stage was showing before 3D took it over, so closing the view puts
// the last render back rather than leaving an empty box.
let stashed: "img" | "video" | null = null;

function show3D(): void {
  if (view3dEl.hidden) stashed = !stageImg.hidden ? "img" : !stageVideo.hidden ? "video" : null;
  stageImg.hidden = true;
  stageVideo.hidden = true; stageVideo.pause();
  view3dEl.hidden = false;
  stage.hidden = false;
  stage.classList.add("stage-3d");
  // Bring the view into sight, clear of the sticky top bar (a click on the run
  // bar often leaves the stage below the fold).
  window.scrollTo({ top: view3dEl.getBoundingClientRect().top + window.scrollY - 64, behavior: "smooth" });
}

function close3D(): void {
  if (view3dEl.hidden) return;
  view?.stop();
  view3dEl.hidden = true;
  view3dEl.classList.remove("expanded");
  $("view3d-expand").textContent = "Expand";
  stage.classList.remove("stage-3d");
  if (stashed === "img") stageImg.hidden = false;
  else if (stashed === "video") stageVideo.hidden = false;
  else stage.hidden = true;
  stashed = null;
}

/** The cutaway selector: one entry per level the scene uses, plus "All". Hidden
 *  for a single-storey scene, where there is nothing to cut open. */
function buildLevels(v: SceneView): void {
  const levels = v.levels();
  const wrap = $("view3d-level-wrap");
  const sel = $<HTMLSelectElement>("view3d-level");
  wrap.hidden = levels.length < 2;
  sel.innerHTML = `<option value="">All levels</option>` +
    levels.map((l) => `<option value="${l}">Up to level ${l}</option>`).join("");
  sel.value = "";
}

/** The control line under the view — what the mouse and keys do right now. */
function reflectHint(): void {
  const walking = view?.currentMode() === "walk";
  const locked = document.pointerLockElement === $("view3d-canvas");
  $("view3d-hint").textContent = !walking
    ? "Drag to orbit · Shift-drag or right-drag to pan · scroll to zoom"
    : locked
      ? "Move the mouse to look · W A S D to move · Q / E down / up · Shift to sprint · Esc to release the mouse"
      : "Click the view, then W A S D to move · drag to look · Q / E down / up · Shift to sprint";
  $("mode-orbit").classList.toggle("on", !walking);
  $("mode-walk").classList.toggle("on", walking);
}

function wire3D(v: SceneView): void {
  $("mode-orbit").addEventListener("click", () => { v.setMode("orbit"); reflectHint(); });
  $("mode-walk").addEventListener("click", () => { v.setMode("walk"); reflectHint(); });
  $("view3d-fit").addEventListener("click", () => { v.fit(); reflectHint(); });
  $<HTMLSelectElement>("view3d-level").addEventListener("change", (e) => {
    const raw = (e.target as HTMLSelectElement).value;
    v.setMaxLevel(raw === "" ? undefined : Number(raw));
  });
  $<HTMLInputElement>("view3d-anim").addEventListener("change", (e) => v.setAnimating((e.target as HTMLInputElement).checked));
  $("view3d-expand").addEventListener("click", () => toggleExpanded());
  $("view3d-close").addEventListener("click", () => close3D());
  window.addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || view3dEl.hidden) return;
    // Escape backs out one step at a time, and never while the JSON is being
    // edited or while the mouse is captured — that press belongs to the pointer
    // lock, which hands the cursor back first.
    const t = e.target as HTMLElement | null;
    if (document.pointerLockElement || (t && (t.tagName === "TEXTAREA" || t.tagName === "INPUT" || t.tagName === "SELECT"))) return;
    if (view3dEl.classList.contains("expanded")) toggleExpanded();
    else close3D();
  });
}

/** Blow the view up to fill the window (and back). */
function toggleExpanded(): void {
  const on = !view3dEl.classList.contains("expanded");
  view3dEl.classList.toggle("expanded", on);
  $("view3d-expand").textContent = on ? "Collapse" : "Expand";
}

// ── Library rail ────────────────────────────────────────────────────────────
async function loadLibrary(): Promise<void> {
  try {
    const [scenes, clips] = await Promise.all([
      fetch("/api/scenes").then((r) => r.json()),
      fetch("/api/clips").then((r) => r.json()),
    ]);
    fillRail("scene-list", "scene-count", scenes.scenes ?? [], (s) => `${s.name} · ${s.placements} placements`, openScene);
    fillRail("clip-list", "clip-count", clips.clips ?? [], (c) => `${c.name} · ${c.duration}s · ${c.shots} shots`, openClip);
  } catch { setStatus("Could not reach the server API — is it running?", "err"); }
}

function fillRail(listId: string, countId: string, items: Record<string, unknown>[], sub: (x: Record<string, unknown>) => string, open: (id: string) => void): void {
  const ul = $(listId); $(countId).textContent = String(items.length);
  ul.innerHTML = "";
  for (const it of items) {
    const li = document.createElement("li");
    li.innerHTML = `<button type="button"><span class="rail-name">${escapeHtml(String(it.id))}</span><span class="rail-sub muted">${escapeHtml(sub(it))}</span></button>`;
    li.querySelector("button")!.addEventListener("click", () => open(String(it.id)));
    ul.appendChild(li);
  }
}

async function openScene(id: string): Promise<void> {
  setStatus("Loading scene…", "busy");
  const data = await fetch(`/api/scenes/${encodeURIComponent(id)}`).then((r) => r.json());
  editor.value = JSON.stringify(data.scene, null, 2); reflectKind(); void render("auto");
}
async function openClip(id: string): Promise<void> {
  setStatus("Loading film…", "busy");
  const data = await fetch(`/api/clips/${encodeURIComponent(id)}`).then((r) => r.json());
  if (!data.clip) { setStatus(data.error || `No film "${id}".`, "err"); return; }
  editor.value = JSON.stringify(data.clip, null, 2); reflectKind();
  void render("board"); // the storyboard is the fast way to see a whole film
}

// ── Examples ────────────────────────────────────────────────────────────────
const EXAMPLE_SCENE = {
  id: "my-scene", name: "My scene", tiles: { w: 8, h: 8 }, ground: "checker",
  placements: [
    { id: "lawn", model: "grass-tile", at: [0, 0], fill: { w: 8, h: 8 } },
    { id: "tree", model: "oak-tree", at: [3, 3] },
    { id: "barrel", model: "barrel", at: [5, 5] },
  ],
};
$("ex-scene").addEventListener("click", () => { editor.value = JSON.stringify(EXAMPLE_SCENE, null, 2); reflectKind(); void render("auto"); });
$("ex-clip").addEventListener("click", () => void openClip("the-wreck"));

// ── Wire up ─────────────────────────────────────────────────────────────────
editor.addEventListener("input", reflectKind);
runBtn.addEventListener("click", () => void render("auto"));
view3dBtn.addEventListener("click", () => void open3D());
boardBtn.addEventListener("click", () => void render("board"));
videoBtn.addEventListener("click", () => void render("video"));
reflectKind();
void loadLibrary();
